import { CompareFn, TableRowSelection } from 'antd/lib/table/interface';
import { fileIcons } from '~src/config/fileTypeIcons';
import {
  DbFolder,
  DbFile,
  DbID,
  DbModuleFile,
  DbTender,
  DbAttachment,
  DbTenderFolder,
  DbProject,
  DbShare,
  DbShareTender,
  DbShareTenderAttachment,
  DbShareTenderFolder,
} from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import { FileVersionsCtx } from '~src/contexts/FileVersionsContext';

import { Dispatch, SetStateAction } from 'react';
import { scrollIntoView } from '~src/utils/ui';

export enum ResourceState {
  NONE,
  DRAFT,
  PARTIAL,
  AVAILABLE,
  ACCEPTED,
  REJECTED,
  COMPLETED,
  PROCESSED,
  VALIDATED,
  REMOVED,
  DELETED,
  EXPIRED,
}

export type TableEntrySourceType =
  | 'archive'
  | 'tender-admin-root'
  | 'tender-admin-inside';

export interface TableEntry {
  key: string;
  folder: boolean;
  moduleFile: boolean;
  type: {
    icon: string;
    iconStyle: string;
    color: string;
    is_empty?: boolean;
  };
  name: string;
  title?: string;
  description?: string;
  notifications?: boolean;
  parentId: string;
  version: string | null;
  createdAt: number;
  updatedAt: number;
  startDate?: number;
  id: DbID;
  state: ResourceState;
  sector?: string;
  subject?: string;
  plan?: string;
  disabled: boolean;
  dbFile?: DbFile;
  recipient?: string | null;
  dbFolder?: DbFolder;
  dbTender?: DbTender;
  dbTenderShare?: DbShare;
  dbProject?: DbProject;
  dbTenderFolder?: DbTenderFolder;
  dbModuleFile?: DbModuleFile;
  dbAttachment?: DbAttachment;
  dbTenderMovement?: MovementEntry;
  dbShare?: DbShare;
  extension?: string;
  favourite?: boolean;
  background?: string;
  isTenderDocuments?: boolean;
  isOnTenderPropertyPanel?: boolean;
  firstElementId?: string | number;
  isUnassigned?: boolean;
  active_from?: number;
  active_until?: number;
  shareId?: string;
  shareKey?: string;
  sourceType?: TableEntrySourceType;
  // Movement-specific properties
  actor?: string;
  summary?: string;
}

export const dbTenderToTableEntry = (dbTender: DbTender): TableEntry => {
  const {
    id,
    title,
    description,
    parent_id,
    created_at,
    updated_at,
    state,
    reference,
    is_favorite,
    is_notify,
    global_serial,
  } = dbTender;

  return {
    key: String(id),
    folder: true,
    moduleFile: false,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'blue',
      is_empty: false,
    },
    name: title || 'Unknown',
    description: description || '',
    notifications: Boolean(is_notify),
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(parent_id),
    id,
    state,
    reference,
    global_serial,
    disabled: state !== ResourceState.AVAILABLE,
    dbTender,
    favourite: Boolean(is_favorite),
  };
};

export const dbTenderShareFileToTableEntry = (data: any): TableEntry => {
  const {
    share_active_from,
    share_description,
    share_expires_at,
    share_id,
    share_key_claimed_at,
    share_key_email,
    share_key_expires_at,
    share_key_first_invite_at,
    share_key_id,
    share_key_is_claimed,
    share_key_last_rejected_at,
    share_key_opened_at,
    share_key_sector_id,
    share_key_state,
    share_key_title,
    share_key_user_id,
    share_state,
    share_title,
    offers,
  } = data;
  return {
    key: String(share_key_id),
    id: data.share_key_id,
    folder: false,
    moduleFile: false,
    name: share_key_title || 'Unknown Share',
    type: {
      icon: 'file',
      iconStyle: 'fas',
      color: 'blue',
      is_empty: false,
    },
    version: null,
    share_key_contact_name: data.share_key_contact_name || '',
    description: data.share_description || '',
    createdAt: data.share_active_from,
    updatedAt: Date.now(),

    offers: offers,
    share_active_from: share_active_from,
    share_description: share_description,
    share_expires_at: share_expires_at,
    share_id: share_id,
    share_key_claimed_at: share_key_claimed_at,
    share_key_email: share_key_email,
    share_key_expires_at: share_key_expires_at,
    share_key_first_invite_at: share_key_first_invite_at,
    share_key_id: share_key_id,
    share_key_is_claimed: share_key_is_claimed,
    share_key_last_rejected_at: share_key_last_rejected_at,
    share_key_opened_at: share_key_opened_at,
    share_key_sector_id: share_key_sector_id,
    share_key_state: share_key_state,
    share_key_title: share_key_title,
    share_key_user_id: share_key_user_id,
    share_state: share_state,
    share_title: share_title,
    parentId: '',
    state: share_key_state,
    disabled: false,
    dbShare: data,
    shareKey: share_key_id,
  };
};

export type MovementEntry = {
  id: number | string;
  originalId?: number;
  type: number;
  created_at: number;
  created_by_user_id: number;
  created_by_user_name: string;
  target_entities: {
    type: string;
    display_name: string;
    description: string | null;
  }[];
  changes: {
    field: string;
    old_value: string;
    new_value: string;
  }[];
  custom?: {
    note: string;
    created_at: number;
    created_by_user_id: number;
    created_by_user_name: string;
  };
  summary?: {
    head: string;
    actions: string[];
  };
  report_data?: {
    is_ignore: boolean;
    is_public: boolean;
    is_report_sent: boolean;
    first_report_datetime: number | null;
    is_in_current_report: boolean;
  };
};

export const dbTenderMovementToTableEntry = (
  movement: MovementEntry
): TableEntry => {
  return {
    key: String(movement.id),
    id: movement.originalId || Number(movement.id),
    folder: false,
    moduleFile: false,
    type: {
      icon: 'file',
      iconStyle: 'fas',
      color: 'purple',
    },
    name: movement.summary?.head || 'Movement',
    description: `Event type: ${movement.type}`,
    actor: movement.created_by_user_name,
    summary: movement.summary?.head || '',
    version: null,
    createdAt: movement.created_at,
    updatedAt: movement.created_at,
    parentId: '',
    state: 0,
    disabled: false,
    dbTenderMovement: movement,
  };
};

export const dbTenderShareToTableEntry = (dbShare: DbShare): TableEntry => {
  const {
    share_id,
    share_title,
    registration_title,
    share_description,
    registration_description,
    share_state,
    project_data,
  } = dbShare;

  return {
    key: share_id,
    folder: true,
    moduleFile: false,
    name: registration_title || share_title || 'Unknown',
    description: share_description || registration_description || '',
    version: null,
    createdAt: project_data?.start_date || Date.now(),
    updatedAt: project_data?.updated_at || Date.now(),
    state: share_state,
    disabled: share_state !== ResourceState.AVAILABLE,
    //Placeholder values to follow up the TableEntry intetrface and avoid making it weaker
    parentId: '',
    id: 0,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'blue',
      is_empty: false,
    },
  };
};

export const dbFolderToTableEntry = (dbFolder: DbFolder): TableEntry => {
  const {
    id,
    name,
    description,
    parent_id,
    created_at,
    updated_at,
    state,
    is_empty,
    is_favorite,
    is_notify,
  } = dbFolder;

  return {
    key: String(id),
    folder: true,
    moduleFile: false,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'yellow',
      is_empty: is_empty,
    },
    name: name || 'Unknown',
    description,
    notifications: is_notify,
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(parent_id),
    id,
    state,
    disabled: state !== DB_STATE.AVAILABLE,
    dbFolder,
    favourite: is_favorite,
  };
};

export const dbFileToTableEntry = (
  dbFile: DbFile,
  sourceType: TableEntrySourceType = 'archive'
): TableEntry => {
  const {
    id,
    title,
    description,
    folder_id,
    created_at,
    state,
    updated_at,
    is_favorite,
    is_notify,
  } = dbFile;
  const nameSections = title && title.split('.');
  const extension =
    nameSections && nameSections.length
      ? nameSections[nameSections.length - 1].toLowerCase()
      : undefined;

  return {
    key: `file-${id}`,
    folder: false,
    moduleFile: false,
    type: (fileIcons[extension || 'default'] ||
      fileIcons.default) as typeof fileIcons.docx,
    extension,
    name: title,
    description,
    notifications: is_notify,
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(folder_id),
    id,
    state,
    disabled: state !== DB_STATE.AVAILABLE,
    dbFile,
    favourite: is_favorite,
    sourceType,
  };
};

export const dbProjectToTableEntry = (dbProject: DbProject): TableEntry => {
  const { id, name, start_date } = dbProject;

  return {
    key: `file-${id}`,
    folder: false,
    moduleFile: false,
    type: fileIcons.default as typeof fileIcons.docx,
    extension: 'default',
    name,
    description: '',
    version: null,
    createdAt: 0,
    updatedAt: 0,
    parentId: String(null),
    id,
    state: DB_STATE.AVAILABLE,
    disabled: false,
    dbProject,
    favourite: false,
    startDate: start_date,
  };
};

export const dbTenderCachedShareToTableEntry = (
  dbProject: DbShareTender,
  shareId: string,
  shareKey: string
): TableEntry => {
  const { title, active_from, active_until } = dbProject;

  return {
    key: `cached-shares-${shareKey}`,
    folder: false,
    moduleFile: false,
    type: fileIcons.default as typeof fileIcons.docx,
    extension: 'default',
    name: title,
    description: '',
    version: null,
    createdAt: 0,
    updatedAt: 0,
    parentId: String(null),
    id: Number(shareKey.replace(/\D/g, '')),
    active_from,
    active_until,
    state: DB_STATE.AVAILABLE,
    disabled: false,
    favourite: false,
    shareId: shareId,
    shareKey: shareKey,
  };
};

export const dbTenderShareFolderToTableEntry = (
  dbProject: DbShareTenderFolder
): TableEntry => {
  const { name, description, id } = dbProject;

  return {
    key: `tender-share-attachment-${name}`,
    folder: false,
    moduleFile: false,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'tenderBlue',
      is_empty: false,
    },
    name: name || 'Unknown',
    description: description || '',
    version: null,
    createdAt: 0,
    updatedAt: 0,
    parentId: String(null),
    id,
    state: DB_STATE.AVAILABLE,
    disabled: false,
    favourite: false,
  };
};

export const dbTenderShareAttachmentToTableEntry = (
  dbProject: DbShareTenderAttachment,
  index: number
): TableEntry => {
  const { name, description } = dbProject;
  const nameSections = (name as string).split('.');
  const extension = nameSections.length
    ? nameSections[nameSections.length - 1].toLowerCase()
    : undefined;

  return {
    key: `tender-share-attachment-${name + index}`,
    folder: false,
    moduleFile: false,
    type: (fileIcons[extension || 'default'] ||
      fileIcons.default) as typeof fileIcons.docx,
    extension,
    name,
    description: description || '',
    version: null,
    createdAt: 0,
    updatedAt: 0,
    parentId: String(null),
    id: Math.random(),
    state: DB_STATE.AVAILABLE,
    disabled: false,
    favourite: false,
  };
};

export const dbModuleFileToTableEntry = (
  dbModuleFile: DbModuleFile
): TableEntry => {
  const { id, plan, sector, subject, description } = dbModuleFile;

  const nameSections = (dbModuleFile.file_name as string).split('.');
  const extension = nameSections.length
    ? nameSections[nameSections.length - 1].toLowerCase()
    : undefined;

  return {
    key: `module-file-${id}`,
    folder: false,
    moduleFile: true,
    type: (fileIcons[extension || 'default'] ||
      fileIcons.default) as typeof fileIcons.docx,
    extension,
    name: dbModuleFile.file_name,
    description,
    notifications: true,
    version: null,
    createdAt: 0,
    updatedAt: 0,
    parentId: String(null),
    id,
    state: DB_STATE.AVAILABLE,
    disabled: false,
    dbModuleFile,
    favourite: false,
    recipient: dbModuleFile.recipient,
    plan,
    sector,
    subject,
  };
};

/** Sorter function - TYPE aka extension column */
export const sortByType: CompareFn<TableEntry> = (a, b) => {
  if (a.folder) {
    // a - folder, b - folder
    if (b.folder) {
      return a.name < b.name ? -1 : 1;
    }

    // a - folder, b - file
    return 1;
  }

  // a - file, b - file
  if (!b.folder) {
    return (a.extension || '') < (b.extension || '') ? -1 : 1;
  }

  // a - file, b - folder
  return -1;
};

/** Sorter function - alphabetical on string property */
export const sortByStringProp: (key: string) => CompareFn<TableEntry> = (
  key
) => (a, b) => {
  if (a[key] === b[key]) {
    return 0;
  }
  if (typeof a[key] === typeof b[key]) {
    return a[key] < b[key] ? -1 : 1;
  }
  return typeof a[key] < typeof b[key] ? -1 : 1;
};

/** Sorter function - numeric order */
export const sortByNumberProp: (key: string) => CompareFn<TableEntry> = (
  key
) => (a, b) => {
  return a[key] - b[key];
};

/** Sorter function - modified (updatedAt), use selected version for files */
export const sortByModified: (
  fileVersionsContext: FileVersionsCtx['fileVersionsContext']
) => CompareFn<TableEntry> = (fileVersionsContext) => (a, b) => {
  let aModified = a.updatedAt;
  // if file, use selected version "modified"
  if (a.dbFile?.versions) {
    aModified =
      fileVersionsContext[a.dbFile.id]?.updated_at ||
      a.dbFile.versions[0]?.updated_at;
  }

  let bModified = b.updatedAt;
  // if file, use selected version "modified"
  if (b.dbFile?.versions) {
    bModified =
      fileVersionsContext[b.dbFile.id]?.updated_at ||
      b.dbFile.versions[0]?.updated_at;
  }

  // compare
  return aModified - bModified;
};

export const isFolderAvailable = (
  folderId: DbFolder['id'] | null,
  foldersList: DbFolder[]
): boolean | null => {
  if (folderId === null) {
    return null;
  }
  const folderIndex = foldersList.findIndex((item) => item.id === folderId);

  if (folderIndex && folderIndex >= 0) {
    if (foldersList && foldersList[folderIndex].state === DB_STATE.AVAILABLE) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const scrollToSelectedItem = (key: string) => {
  // get row elem & scroll
  const rowElem = document.querySelector(
    `.ant-table-row.ant-table-row-selected[data-row-key="${key}"]`
  );

  if (rowElem?.scrollIntoView) {
    setTimeout(() => scrollIntoView(rowElem), 100);
  }
};

// interface HandleSelectShiftProps {
//   record: TableEntry;
//   index?: number;
//   selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
//   setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
//   tableData: TableEntry[];
// }

// export const handleSelectShift = ({
//   record,
//   index,
//   selectedKeys,
//   setSelectedItems,
//   tableData,
// }: HandleSelectShiftProps) => {
//   if (selectedKeys && selectedKeys.length === 0) {
//     return setSelectedItems((prevSelectedItems) => [
//       record,
//       ...prevSelectedItems,
//     ]);
//   }
//   if (selectedKeys && selectedKeys.length > 0) {
//     const selectedIndex = selectedKeys.findIndex(
//       (element) => element === record.key
//     );
//     if (selectedIndex >= 0) {
//       return setSelectedItems((prevItems) =>
//         prevItems.filter((item) => item.key !== record.key)
//       );
//     }
//     const tableKeys = tableData.map((item) => item.key);

//     const isAnySelectedInCurrentView = selectedKeys.some((el) =>
//       tableKeys.includes(el as string)
//     );
//     if (!isAnySelectedInCurrentView) {
//       return setSelectedItems((prevSelectedItems) => [
//         record,
//         ...prevSelectedItems,
//       ]);
//     }

//     const lastIndexReversed = tableData
//       .reverse()
//       .findIndex((el) => selectedKeys.includes(el.key));

//     const lastIndex = tableData.findIndex(
//       (el) => el.key === tableData.reverse()[lastIndexReversed].key
//     );
//     const firstIndex = tableData.findIndex((el) =>
//       selectedKeys.includes(el.key)
//     );

//     if ((index as number) >= 0) {
//       if ((index as number) < firstIndex) {
//         return setSelectedItems((prevState) => [
//           ...prevState,
//           ...tableData.slice(index, firstIndex),
//         ]);
//       }

//       if ((index as number) > lastIndex) {
//         return setSelectedItems((prevState) => [
//           ...prevState,
//           ...tableData.slice(lastIndex + 1, (index as number) + 1),
//         ]);
//       }

//       if ((index as number) > firstIndex && (index as number) < lastIndex) {
//         return setSelectedItems((prevState) =>
//           uniq([
//             ...prevState,
//             ...tableData.slice(firstIndex, (index as number) + 1),
//           ])
//         );
//       }
//     }
//   }
// };

interface HandleSelectCtrlProps {
  record: TableEntry;
  selectedItems: TableEntry[];
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
}

export const handleSelectCtrl = ({
  record,
  selectedItems,
  setSelectedItems,
}: HandleSelectCtrlProps) => {
  const selectedIndex = selectedItems.findIndex(
    (element) => element.id === record.id
  );
  if (selectedIndex >= 0) {
    setSelectedItems((prevSelectedItems) =>
      prevSelectedItems.filter((item) => item.key !== record.key)
    );
  } else {
    setSelectedItems((prevSelectedItems) => {
      if (!prevSelectedItems.length) {
        // delay as we are within setState
        setTimeout(() => scrollToSelectedItem(record.key), 200);
      }
      return [record, ...prevSelectedItems];
    });
  }
};
